import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react"

type SurveyStorageState = Record<string, string>

type SurveyStorageContextValue = {
  data: SurveyStorageState
  setItem: (key: string, value: string) => void
  getItem: (key: string) => string | undefined
  removeItem: (key: string) => void
  reset: () => void
}

const SurveyStorageContext = createContext<
  SurveyStorageContextValue | undefined
>(undefined)

const STORAGE_PREFIX = "survey-storage:"

type SurveyStorageProviderProps = {
  surveyId: string
  children: ReactNode
}

function loadInitialState(surveyId: string): SurveyStorageState {
  if (typeof window === "undefined") return {}

  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${surveyId}`)
    if (!raw) return {}

    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === "object") {
      const result: SurveyStorageState = {}

      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === "string") {
          result[key] = value
        }
      }

      return result
    }
  } catch {}

  return {}
}

export const SurveyStorageProvider: React.FC<SurveyStorageProviderProps> = ({
  surveyId,
  children,
}) => {
  const [data, setData] = useState<SurveyStorageState>(() =>
    loadInitialState(surveyId),
  )

  useEffect(() => {
    if (typeof window === "undefined") return

    try {
      window.localStorage.setItem(
        `${STORAGE_PREFIX}${surveyId}`,
        JSON.stringify(data),
      )
    } catch {}
  }, [surveyId, data])

  const value = useMemo<SurveyStorageContextValue>(
    () => ({
      data,
      setItem: (key, value) => {
        setData((prev) =>
          prev[key] === value ? prev : { ...prev, [key]: value },
        )
      },
      getItem: (key) => data[key],
      removeItem: (key) => {
        setData((prev) => {
          if (!(key in prev)) return prev
          const next = { ...prev }
          delete next[key]
          return next
        })
      },
      reset: () => setData({}),
    }),
    [data],
  )

  return (
    <SurveyStorageContext.Provider value={value}>
      {children}
    </SurveyStorageContext.Provider>
  )
}

export function useSurveyStorage(): SurveyStorageContextValue {
  const ctx = useContext(SurveyStorageContext)
  if (!ctx) {
    throw new Error(
      "useSurveyStorage should be used only inside <SurveyStorageProvider>",
    )
  }
  return ctx
}
